import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { MovementAreaStatus, TeamGroups } from "../../demo/components/TeamGroups";
import { Link, useSearchParams } from "react-router-dom";
import s from "./Map.module.css";
import { FULLSCREEN_STYLE, useFullscreen } from "../shared/useFullscreen";
import { AirfieldSvg, type TeamMarker } from "./AirfieldSvg";
import { loadBridge, type BaseMode } from "./RealBase";
import type { Target, TeamRow } from "./realBridge";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import {
  useAlerts,
  useInspectionDetails,
  useInspections,
  useMapFeatures,
  useMapLayers,
  useWorkOrders,
} from "../../api/queries";
import { centroidOf, projectionAbout, styleFor, type LonLat, type MapFeature } from "../../domain/geo";
import { sortInspections } from "../../domain/inspections";
import {
  Card,
  ErrorState,
  PriorityBadge,
  Segmented,
  SeverityBadge,
  Skeleton,
  StatusBadge,
} from "../../design/components";
import { formatDateTime } from "../../lib/format";
import {
  MARKER_COLOUR,
  MARKER_LABEL,
  placeAlerts,
  placeFindings,
  placeWorkOrders,
  tagIndex,
  type Marker,
  type MarkerKind,
} from "./markers";
import type { WorkOrder } from "../../domain/workOrders";
import type { Alert } from "../../domain/alerts";
import { t, tn, tc } from "../../i18n";
import { loadDemo } from "../../demo/domain/load";

type Window = "24h" | "7d" | "all";
const WINDOW_MS: Record<Window, number | null> = { "24h": 86_400_000, "7d": 7 * 86_400_000, all: null };

const INTERACTIVE = ["agl", "signs", "buildings", "pavement"];

/**
 * The airfield, drawn from the platform's own geometry, with the work on it.
 * Pack layers are toggled on the left; operational layers (work orders,
 * alerts, inspection failures) are placed by point, asset tag or zone; the
 * right-hand panel shows what is selected and where it leads.
 */
type Bridge = Awaited<ReturnType<typeof loadBridge>>;

/** The aerodrome the survey sheet belongs to: its dynamic map is the Interactive Map itself. */
export const SHEET_ICAO = "OEMA";
export const hasSheet = (siteCode: string) => siteCode.toUpperCase() === SHEET_ICAO;

/** The ISO day a window starts, for the engine's work-order filter; undefined shows everything open. */
export function windowSince(w: Window, serverTime: string): string | undefined {
  const ms = WINDOW_MS[w];
  return ms ? new Date(new Date(serverTime).getTime() - ms).toISOString().slice(0, 10) : undefined;
}

/*
 * The map imports the boards' engine statically, so it is in memory once this
 * resolves — but `load.ts` only records that inside `loadDemo()`. Without it,
 * everything queued for the engine (closures and work orders from the
 * platform, the map's records going to the platform) waited for a load the
 * page never asked for, whenever /airfield was the first screen opened. The
 * call downloads nothing further; see `board()` in the router.
 */
const EngineMap = lazy(async () => {
  const m = await import("../../demo/boards/AirfieldMap");
  await loadDemo();
  return { default: m.AirfieldMap };
});

/**
 * The Interactive Map, as the Operations Control suite shows it: the same
 * engine, the same views, the dispatch bar across the top of the frame, and
 * the page's time window applied to the work orders it draws.
 */
function SheetMap({ window, serverTime }: { window: Window; serverTime: string }) {
  useEffect(() => {
    let live = true;
    Promise.all([import("../../demo/domain/tables"), import("../../demo/lib/repaint")]).then(([tables, r]) => {
      if (!live) return;
      tables.WO_FILTER.since = windowSince(window, serverTime);
      r.requestRepaint();
    });
    return () => {
      live = false;
      import("../../demo/domain/tables").then((tables) => {
        tables.WO_FILTER.since = undefined;
      });
    };
  }, [window, serverTime]);
  return (
    <Suspense
      fallback={
        <div className={s.stage}>
          <div className={s.empty}>{t("Loading the map…")}</div>
        </div>
      }
    >
      <div className={s.engine} dir="ltr">
        <EngineMap bar initialMode="day" />
      </div>
    </Suspense>
  );
}

export function MapPage() {
  const { site, serverTime, principal } = useSession();
  const [params, setParams] = useSearchParams();
  const layersQ = useMapLayers();
  const layers = useMemo(() => layersQ.data ?? [], [layersQ.data]);

  /* Visibility: the pack's defaults until the user touches a toggle, then their choice. */
  const [chosen, setVisible] = useState<Set<string> | null>(null);
  const defaults = useMemo(() => {
    const v = new Set(layers.filter((l) => l.visibleDefault && l.featureCount > 0).map((l) => l.key));
    /* A site with located assets but no drawing still gets a map. */
    if (!v.size) layers.forEach((l) => l.featureCount > 0 && v.add(l.key));
    return v;
  }, [layers]);
  const visible = chosen ?? defaults;
  const [ops, setOps] = useState<Set<MarkerKind>>(() => new Set<MarkerKind>(["work_order", "alert", "finding"]));
  const window = (params.get("window") as Window) || "all";
  const selectedId = params.get("sel") ?? undefined;

  /* Every layer with features is fetched (they are small and cached for the session); visibility only filters the draw. */
  const fetchable = useMemo(() => layers.filter((l) => l.featureCount > 0).map((l) => l.key), [layers]);
  const featureQs = useMapFeatures(fetchable);
  const allFeatures = useMemo(() => featureQs.flatMap((q) => q.data ?? []), [featureQs]);
  const drawn = useMemo(() => allFeatures.filter((f) => visible?.has(f.layer)), [allFeatures, visible]);
  const loadingFeatures = featureQs.some((q) => q.isPending);

  const projection = useMemo(() => {
    const c = site.centre ? ([site.centre.lon, site.centre.lat] as [number, number]) : centroidOf(allFeatures);
    return projectionAbout(c ?? [0, 0]);
  }, [site.centre, allFeatures]);

  const orders = useWorkOrders();
  const alerts = useAlerts();
  const rounds = useInspections({ status: "completed" });
  const recentRoundIds = useMemo(
    () =>
      sortInspections(rounds.data ?? [])
        .filter((r) => r.failures > 0)
        .slice(0, 10)
        .map((r) => r.id),
    [rounds.data],
  );
  const roundDetails = useInspectionDetails(recentRoundIds);

  const placement = useMemo(() => {
    const tags = tagIndex(allFeatures);
    const ms = WINDOW_MS[window];
    const since = ms ? new Date(new Date(serverTime).getTime() - ms) : undefined;
    const wo = placeWorkOrders(orders.data?.items ?? [], tags, since);
    const al = placeAlerts(alerts.data ?? [], tags, since);
    const fi = placeFindings(
      roundDetails.flatMap((q) => (q.data ? [q.data] : [])),
      tags,
      since,
    );
    return {
      markers: [
        ...(ops.has("finding") ? fi.markers : []),
        ...(ops.has("alert") ? al.markers : []),
        ...(ops.has("work_order") ? wo.markers : []),
      ],
      counts: { work_order: wo.markers.length, alert: al.markers.length, finding: fi.markers.length } as Record<
        MarkerKind,
        number
      >,
      unplaced: [...wo.unplaced, ...al.unplaced, ...fi.unplaced],
    };
  }, [allFeatures, orders.data, alerts.data, roundDetails, ops, window, serverTime]);

  const selected = placement.markers.find((m) => m.id === selectedId);
  const select = (id: string | undefined) => {
    const next = new URLSearchParams(params);
    if (id) next.set("sel", id);
    else next.delete("sel");
    setParams(next, { replace: true });
    if (id) setClicked(null);
  };

  /* ── the same airfield as the Interactive Map, and the teams on it ───── */
  const [base, setBase] = useState<BaseMode>("drawing");
  const [bridge, setBridge] = useState<Bridge | null>(null);
  const [clicked, setClicked] = useState<{ at: LonLat } | null>(null);
  const [, tick] = useState(0);
  const { ref: frameRef, active: fullActive, toggle: toggleFull } = useFullscreen<HTMLDivElement>();
  useEffect(() => {
    if (hasSheet(site.code)) return; /* the engine map runs its own fleet clock */
    let live = true;
    loadBridge().then((b) => {
      if (!live) return;
      b.runFleet();
      setBridge(b);
    });
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [site.code]);
  const sheet = hasSheet(site.code);
  const rows: TeamRow[] = bridge && !sheet ? bridge.teamRows() : [];
  const teams: TeamMarker[] = bridge
    ? rows
        .filter((row) => row.state !== "offduty")
        .map((row) => {
          const ll = bridge.toLonLat({ E: row.E, N: row.N });
          return {
            k: row.k,
            name: row.name,
            at: [ll.lon, ll.lat],
            colour: row.ring,
            label: row.moveLabel || row.label,
            moving: row.spd > 0,
          };
        })
    : [];
  /* what a team is sent to: the selected item, or the point clicked on the map */
  const target: { t: Target; label: string } | null = useMemo(() => {
    if (!bridge) return null;
    if (clicked) {
      const en = bridge.toEN({ lon: clicked.at[0], lat: clicked.at[1] });
      return {
        t: { kind: "point", E: en.E, N: en.N, label: "a point on the map" },
        label: `${Math.round(en.E)} E ${Math.round(en.N)} N`,
      };
    }
    if (selected) {
      const en = bridge.toEN({ lon: selected.at[0], lat: selected.at[1] });
      return { t: { kind: "point", E: en.E, N: en.N, label: selected.title }, label: selected.title };
    }
    return null;
  }, [bridge, clicked, selected]);
  const send = (k: string) => {
    if (!bridge || !target) return;
    bridge.dispatch(k, target.t, principal.name || "Operator");
    tick((n) => n + 1);
  };
  const recallTeam = (k: string) => {
    bridge?.recall(k);
    tick((n) => n + 1);
  };
  const inspect = (k: string, rwy: string) => {
    bridge?.inspectRunway(k, rwy);
    tick((n) => n + 1);
  };
  const setWindow = (w: Window) => {
    const next = new URLSearchParams(params);
    if (w === "all") next.delete("window");
    else next.set("window", w);
    setParams(next, { replace: true });
  };

  return (
    <Page
      eyebrow={t("{site} · airfield", { site: site.code })}
      title="Dynamic map"
      actions={
        <Segmented<Window>
          ariaLabel="Time window"
          value={window}
          options={[
            { value: "24h", label: "Last 24 h" },
            { value: "7d", label: "Last 7 days" },
            { value: "all", label: "All open" },
          ]}
          onChange={setWindow}
        />
      }
    >
      {sheet ? (
        <SheetMap window={window} serverTime={serverTime} />
      ) : (
        <div className={s.layout}>
          <Card title="Layers">
            {layersQ.isPending ? (
              <Skeleton rows={6} />
            ) : layersQ.isError ? (
              <ErrorState error={layersQ.error} retry={() => void layersQ.refetch()} />
            ) : (
              <div className={s.panel}>
                <div className={s.groupLabel}>{t("Operations")}</div>
                {(["work_order", "alert", "finding"] as MarkerKind[]).map((k) => (
                  <label key={k} className={s.toggle}>
                    <input
                      type="checkbox"
                      checked={ops.has(k)}
                      onChange={(e) => {
                        const next = new Set(ops);
                        if (e.target.checked) next.add(k);
                        else next.delete(k);
                        setOps(next);
                      }}
                    />
                    <span
                      className={`${s.swatch} ${s.swatchDot}`}
                      style={{ background: MARKER_COLOUR[k], borderColor: MARKER_COLOUR[k] }}
                    />
                    <span>{t(MARKER_LABEL[k])}</span>
                    <span className={s.count}>{placement.counts[k]}</span>
                  </label>
                ))}
                <div className={s.groupLabel}>{t("Drawing")}</div>
                {layers.map((l) => {
                  const st = styleFor(l, l.key);
                  return (
                    <label key={l.key} className={s.toggle}>
                      <input
                        type="checkbox"
                        checked={visible?.has(l.key) ?? false}
                        disabled={l.featureCount === 0}
                        onChange={(e) => {
                          const next = new Set(visible);
                          if (e.target.checked) next.add(l.key);
                          else next.delete(l.key);
                          setVisible(next);
                        }}
                      />
                      <span
                        className={s.swatch}
                        style={{
                          background: st.fill ?? "transparent",
                          borderColor: st.stroke ?? st.fill ?? "var(--line)",
                        }}
                      />
                      <span>{l.name}</span>
                      <span className={s.count}>{l.featureCount}</span>
                    </label>
                  );
                })}
                {placement.unplaced.length > 0 && (
                  <>
                    <div className={s.groupLabel}>{t("Not placeable")}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {tn(
                        placement.unplaced.length,
                        "{n} item carry no position or a tag the drawing does not know; listed on the right.",
                        "{n} items carry no position or a tag the drawing does not know; listed on the right.",
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>

          <div
            ref={frameRef}
            className={`${s.frame} ${fullActive ? s.full : ""}`}
            style={fullActive ? FULLSCREEN_STYLE : undefined}
          >
            <DispatchBar
              base={base}
              onBase={setBase}
              full={fullActive}
              onFull={() => void toggleFull()}
              rows={rows}
              target={target?.label ?? null}
              runways={bridge ? bridge.sheet().runways.map((r) => r.designators) : []}
              onSend={send}
              onRecall={recallTeam}
              onInspect={inspect}
            />
            {loadingFeatures && allFeatures.length === 0 ? (
              <div className={s.stage}>
                <div className={s.empty}>{t("Loading the drawing…")}</div>
              </div>
            ) : (
              <AirfieldSvg
                layers={layers}
                features={drawn}
                markers={placement.markers}
                projection={projection}
                selected={selectedId}
                onSelect={select}
                interactiveLayers={INTERACTIVE}
                base={base}
                onMapClick={(p) => setClicked({ at: p })}
                teams={teams}
              />
            )}
          </div>

          <div className={s.side}>
            {selected ? (
              <SelectedCard m={selected} onClear={() => select(undefined)} />
            ) : (
              <Card
                title="On the map"
                sub={tn(placement.markers.length, "{n} item in the window", "{n} items in the window")}
              >
                {placement.markers.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13.5 }}>
                    {t("Nothing open in this window at {site}.", { site: site.code })}
                  </div>
                ) : (
                  <div className={s.sideList}>
                    {[...placement.markers]
                      .sort((a, b) => a.weight - b.weight || b.when.localeCompare(a.when))
                      .slice(0, 14)
                      .map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className={s.sideRow}
                          onClick={() => select(m.id)}
                          style={{ background: "transparent", textAlign: "start" }}
                        >
                          <span
                            className={`${s.swatch} ${s.swatchDot}`}
                            style={{
                              background: MARKER_COLOUR[m.kind],
                              borderColor: MARKER_COLOUR[m.kind],
                              marginTop: 3,
                            }}
                          />
                          <span>
                            {m.title}
                            <div className={s.sideMeta}>{m.subtitle}</div>
                          </span>
                        </button>
                      ))}
                  </div>
                )}
              </Card>
            )}
            {placement.unplaced.length > 0 && (
              <Card title="Not placeable" sub="no position, and no tag the drawing knows">
                <div className={s.sideList}>
                  {placement.unplaced.slice(0, 8).map((u, i) => (
                    <div key={i} className={s.sideRow} style={{ cursor: "default" }}>
                      <span
                        className={`${s.swatch} ${s.swatchDot}`}
                        style={{ background: MARKER_COLOUR[u.kind], borderColor: MARKER_COLOUR[u.kind], marginTop: 3 }}
                      />
                      <span className="muted">{u.title}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}

/**
 * The dispatch bar, across the top of the frame: how the airfield is shown,
 * full screen, what is targeted, and every team with a SEND. RFF and security
 * go as P1, the rest as a call-out; each dispatch is a work order the platform
 * sees. Positions are the engine's simulation until a vehicle feed arrives.
 */
export function DispatchBar({
  base,
  onBase,
  full,
  onFull,
  rows,
  target,
  runways,
  onSend,
  onRecall,
  onInspect,
}: {
  base: BaseMode;
  onBase: (b: BaseMode) => void;
  full: boolean;
  onFull: () => void;
  rows: TeamRow[];
  target: string | null;
  runways: string[];
  onSend: (k: string) => void;
  onRecall: (k: string) => void;
  onInspect: (k: string, rwy: string) => void;
}) {
  const rff = rows.filter((row) => /fire/i.test(row.kind));
  return (
    <div className={s.bar} role="toolbar" aria-label={t("Dispatch")}>
      <Segmented<BaseMode>
        ariaLabel="How the airfield is shown"
        value={base}
        options={[
          { value: "drawing", label: "Drawing" },
          { value: "day", label: "Real day" },
          { value: "night", label: "Real night" },
        ]}
        onChange={onBase}
      />
      <button type="button" className={`${s.team}`} onClick={onFull} aria-pressed={full}>
        {full ? t("EXIT FULL SCREEN") : t("FULL SCREEN")}
      </button>
      <span className={s.barLabel}>{tc("Target", "map")}</span>
      <span className={s.target}>{target ?? t("click an item or any point on the map")}</span>
      <button
        type="button"
        className={s.team}
        style={{ color: "#ff6a66", borderColor: "#6a2a28" }}
        disabled={!target || !rff.length}
        onClick={() => rff.forEach((row) => onSend(row.k))}
      >
        {t("EMERGENCY · ALL RFF")}
      </button>
      <TeamGroups
        rows={rows}
        canSend={!!target}
        runways={runways}
        onSend={onSend}
        onRecall={onRecall}
        onInspect={onInspect}
      />
      <MovementAreaStatus rows={rows} />
      <span className={s.barNote}>
        {t(
          "TEAM POSITIONS ARE SIMULATED UNTIL A VEHICLE FEED IS CONNECTED · DERIVED ITEMS ARE MARKED",
        )}
      </span>
    </div>
  );
}

function SelectedCard({ m, onClear }: { m: Marker; onClear: () => void }) {
  if (m.kind === "work_order") {
    const w = m.ref as WorkOrder;
    return (
      <Card
        title={
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <PriorityBadge priority={w.priority} /> <span className="mono">{w.number}</span>
          </span>
        }
        sub={w.title}
        actions={
          <button
            type="button"
            className="muted"
            style={{ background: "none", border: 0, cursor: "pointer" }}
            onClick={onClear}
            aria-label={t("Clear selection")}
          >
            ✕
          </button>
        }
      >
        <dl className={s.kv}>
          <dt>{t("Status")}</dt>
          <dd>
            <StatusBadge status={w.status} />
          </dd>
          <dt>{t("Kind")}</dt>
          <dd>{w.kind}</dd>
          <dt>{t("Zone")}</dt>
          <dd className="mono">{w.zoneCode ?? "—"}</dd>
          <dt>{t("Asset")}</dt>
          <dd className="mono">{w.assetTag ?? "—"}</dd>
          <dt>{t("Crew")}</dt>
          <dd>{w.crew ?? t("unassigned")}</dd>
          <dt>{t("Opened")}</dt>
          <dd>{formatDateTime(w.openedAt)}</dd>
        </dl>
        {w.description && (
          <p className="muted" style={{ fontSize: 13.5, marginTop: 10 }}>
            {w.description}
          </p>
        )}
        <div style={{ marginTop: 10 }}>
          <Link to={m.href}>{t("Open work order →")}</Link>
        </div>
      </Card>
    );
  }
  if (m.kind === "alert") {
    const a = m.ref as Alert;
    return (
      <Card
        title={
          <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <SeverityBadge severity={a.severity} /> {t("Alert")}
          </span>
        }
        sub={a.title}
        actions={
          <button
            type="button"
            className="muted"
            style={{ background: "none", border: 0, cursor: "pointer" }}
            onClick={onClear}
            aria-label={t("Clear selection")}
          >
            ✕
          </button>
        }
      >
        <dl className={s.kv}>
          <dt>{t("Kind")}</dt>
          <dd>{a.kind}</dd>
          <dt>{t("Rule")}</dt>
          <dd className="mono">{a.ruleKey ?? "—"}</dd>
          <dt>{t("Asset")}</dt>
          <dd className="mono">{a.assetTag ?? "—"}</dd>
          <dt>{t("Raised")}</dt>
          <dd>{formatDateTime(a.raisedAt)}</dd>
          {a.predictedFor && (
            <>
              <dt>{t("Expected")}</dt>
              <dd>{formatDateTime(a.predictedFor)}</dd>
            </>
          )}
        </dl>
        <div style={{ marginTop: 10 }}>
          <Link to={m.href}>{t("Why did this fire? →")}</Link>
        </div>
      </Card>
    );
  }
  const { inspection, observation } = m.ref as Exclude<Marker["ref"], WorkOrder | Alert>;
  return (
    <Card
      title="Inspection failure"
      sub={m.title}
      actions={
        <button
          type="button"
          className="muted"
          style={{ background: "none", border: 0, cursor: "pointer" }}
          onClick={onClear}
          aria-label={t("Clear selection")}
        >
          ✕
        </button>
      }
    >
      <dl className={s.kv}>
        <dt>{t("Round")}</dt>
        <dd>{inspection.templateName}</dd>
        <dt>{t("When")}</dt>
        <dd>{formatDateTime(inspection.completedAt ?? inspection.startedAt)}</dd>
        <dt>{t("Zone")}</dt>
        <dd className="mono">{observation.zoneCode ?? "—"}</dd>
        <dt>{t("Asset")}</dt>
        <dd className="mono">{observation.assetTag ?? "—"}</dd>
        <dt>{t("Defect")}</dt>
        <dd className="mono">{observation.defectId ?? "—"}</dd>
        <dt>{t("Photos")}</dt>
        <dd>{observation.photos}</dd>
      </dl>
      {observation.notes && (
        <p className="muted" style={{ fontSize: 13.5, marginTop: 10 }}>
          {observation.notes}
        </p>
      )}
      <div style={{ marginTop: 10 }}>
        <Link to={m.href}>{t("Open the round →")}</Link>
      </div>
    </Card>
  );
}

export type { MapFeature };
